<script setup>
import { ref, watch, onBeforeMount } from 'vue'
import { useAuth } from '@/stores/auth'
import { useAccount } from '@/stores/account'
import { useOrganization } from '@/stores/organization'
import { useChecker } from '@/stores/checker'
import { useBilling } from '@/stores/billing'
import { useRouter, useRoute } from 'vue-router'
import debounce from 'lodash.debounce'

import Info from '@/components/elements/Info.vue'
import MainInput from '@/components/elements/MainInput.vue'
import PreferenceLogo from '@/components/preference/PreferenceLogo.vue'

onBeforeMount(async () => {
  pageLoaded.value = false
  loadRequiredData()
})

const auth = useAuth()
const account = useAccount()
const organization = useOrganization()
const checker = useChecker()
const billingStore = useBilling()
const router = useRouter()
const route = useRoute()

const sessionId = route.query.session_id || null

const surveyStep = ref('start')
const surveyStepBuffer = ref(null)
const pageLoaded = ref(false)
const selectedType = ref(null)
const sideMenuStep = ref([
  { name: '1. Start', active: true },
  { name: '2. Audience size', active: false },
  { name: '3. About you', active: false },
])
const typeList = ref([
  {
    name: 'Integrations',
    active: false,
    description: 'Synchronize data and execute actions between platforms in real time.',
    id: 'integrations',
    nextStep: 'integrations-step',
  },
  {
    name: 'Preference center',
    active: false,
    description: 'Create a hub where your users can manage their email preferences.',
    id: 'preference-pages',
    nextStep: 'contacts-step',
  },
])
const selectedIntegrations = ref([])
const integrationsList = ref([
  { name: 'ActiveCampaign', active: false },
  { name: 'SendGrid', active: false },
  { name: 'ConvertKit', active: false },
  { name: 'Campaign Monitor', active: false },
  { name: 'Brevo', active: false },
  { name: 'HubSpot', active: false },
  { name: 'Mailchimp', active: false },
  { name: 'Sendlane', active: false },
  { name: 'Drip', active: false },
  { name: 'Salesforce', active: false },
  { name: 'Other', active: false },
])
const extraIntegration = ref(null)
const otherInputBox = ref(null)
const selectedContacts = ref(null)
const contactAmount = ref([
  { name: '1 to 1,000', active: false, value: 1 },
  { name: '1,001 to 5,000', active: false, value: 1000 },
  { name: '5,001 to 15,000', active: false, value: 5000 },
  { name: '15,001 to 50,000', active: false, value: 15000 },
  { name: '50,001 to 100,000', active: false, value: 50000 },
  { name: '100,001 to 250,000', active: false, value: 100000 },
  { name: '250,000+', active: false, value: 250000 },
])
const businessName = ref(null)
const domainName = ref(null)
const fullDomain = ref(null)
const inputError = ref(null)
const inputSuccess = ref(null)
const creatingExplainedText = ref('Sit tight while we set up your account!')
const validPathExamples = ref(`a, bc, 1, 23, DE, fg-4-hi, jk-LM, no56PQ`)

async function loadRequiredData() {
  await account.get(auth.id)
  pageLoaded.value = true
}

function selectType(type, index, step) {
  selectedType.value = type.id
  typeList.value.forEach((elm) => (elm.active = false))
  typeList.value[index].active = true
  surveyStepBuffer.value = step
}

function selectIntegration(integration, index) {
  if (selectedIntegrations.value.includes(integration)) {
    let toRemove = selectedIntegrations.value.indexOf(integration)
    selectedIntegrations.value.splice(toRemove, 1)
    integrationsList.value[index].active = false
    if (integration === 'Other') {
      otherInputBox.value = null
    }
  } else {
    selectedIntegrations.value.push(integration)
    integrationsList.value[index].active = true
    if (integration === 'Other') {
      otherInputBox.value = true
    }
  }
}

function selectContact(selection, index) {
  selectedContacts.value = selection.name
  contactAmount.value.forEach((option) => (option.active = false))
  contactAmount.value[index].active = true
}

function continueStepIntegration(step) {
  extraIntegration.value ? selectedIntegrations.value.push(extraIntegration.value) : null
  continueStep(step)
}

function continueStep(step) {
  inputError.value = null
  inputSuccess.value = null
  updateSideMenu(step)

  surveyStep.value = step
}

function updateSideMenu(step) {
  sideMenuStep.value.forEach((elm) => (elm.active = false))
  switch (step) {
    case 'integrations-step':
      sideMenuStep.value[0].active = true
      break
    case 'contacts-step':
      sideMenuStep.value[1].active = true
      break
    case 'third-step':
      sideMenuStep.value[2].active = true
      break
    case 'fourth-step':
      sideMenuStep.value[2].active = true
      break
    case 'fifth-step':
      sideMenuStep.value[3].active = true
      break
  }
}

async function finishSurvey(step) {
  creatingExplainedText.value = 'Submitting your data...'
  inputError.value = null
  inputSuccess.value = null
  surveyStep.value = step

  const surveyData = {
    version: 1,
    data: {
      name: businessName.value,
      domain: domainName.value,
      integrations: selectedIntegrations.value,
      contacts: selectedContacts.value,
    },
  }

  try {
    account.surveySubmit(auth.id, surveyData)
    createOrganization().then(() => {
      router.replace({ name: 'dashboard' })
    })
  } catch {
    return
  }
}

async function createOrganization() {
  creatingExplainedText.value = 'Creating your organization...'
  const orgData = {
    name: businessName.value,
  }
  const org = await organization.create(orgData)

  await account.update(auth.id, { organization: org.id }).then(async () => {
    account.organization = org.id
    await auth.updateTokenOrg(org.id)
  })

  const domainData = { 'domain': domainName.value, 'name': domainName.value }
  await organization.createSite(domainData)

  // Claim checkout session if present (non-blocking: a failure here shouldn't stop the
  // user from reaching the dashboard, since the subscription can be resolved manually —
  // but it must not fail silently, or a real bug here (as happened once already) goes
  // completely unnoticed. Report it instead of swallowing it.
  if (sessionId) {
    try {
      await billingStore.claimCheckout(sessionId)
    } catch (err) {
      import('@sentry/vue').then((Sentry) => {
        Sentry.captureException(err, { extra: { sessionId, context: 'claimCheckout' } })
      })
    }
  }
}

watch(businessName, () => {
  bizName()
})

const bizName = debounce(async () => {
  inputError.value = null
  inputSuccess.value = null

  if (businessName.value == '') {
    inputError.value = 'This empty name is already taken 💔'
    return
  }

  const checkResultName = await checker.getOrganization(businessName.value)
  if (checkResultName[0].error) {
    inputError.value = 'This name is already taken'
  } else {
    inputSuccess.value = 'This name is available'
  }
}, 500)

watch(domainName, () => {
  domName()
})

const domName = debounce(async () => {
  let regex = /^((?!-)[A-Za-z0-9-]{1,63}(?<!-))$/

  inputError.value = null
  inputSuccess.value = null

  if (regex.test(domainName.value)) {
    inputError.value = null
  } else {
    inputError.value = `'${domainName.value}' is an invalid domain. The domain should only contain letters, numbers, and may contain dashes between words`
    return
  }

  fullDomain.value = domainName.value.concat('.emailpref.com')

  const checkDomainName = await checker.getDomain(fullDomain.value)
  if (checkDomainName[0].error) {
    inputError.value = 'This domain is already taken'
  } else {
    inputSuccess.value = 'This domain is available'
  }
}, 500)

function backBtn(step) {
  if (step == 'start') {
    selectedIntegrations.value = []
    selectedContacts.value = null
    businessName.value = null
    domainName.value = null
    contactAmount.value.forEach((option) => (option.active = false))
    integrationsList.value.forEach((option) => (option.active = false))
  }
  inputError.value = null
  inputSuccess.value = null
  surveyStep.value = step
  updateSideMenu(step)
}

function logout() {
  auth.logout()
  router.push({ name: 'login' })
}
</script>

<template>
  <div v-if="pageLoaded" class="h-screen md:flex">
    <div class="w-full h-1/5 md:h-screen md:w-1/5">
      <div class="flex flex-col bg-gray-200 shadow-inner h-full">
        <div class="flex flex-col h-full w-full items-center justify-center">
          <div class="my-3 md:mb-10"><PreferenceLogo /></div>
          <div class="space-y-1">
            <p v-for="item in sideMenuStep" :key="item.name" :class="[item.active ? 'font-semibold' : 'text-gray-500, max-md:hidden']">
              {{ item.name }}
            </p>
          </div>
        </div>
        <div class="flex w-full justify-center">
          <div class="text-sm text-gray-600 pb-5 cursor-pointer hover:text-ep-blue-500" @click="logout()">Logout</div>
        </div>
      </div>
    </div>

    <div class="w-full h-4/5 md:h-screen md:w-4/5">
      <div class="h-full bg-white">
        <div v-if="surveyStep === 'start'" class="flex flex-col h-full w-full items-center justify-center px-10">
          <div class="space-y-10">
            <div class="flex flex-col items-center text-center">
              <h1 class="text-ep-blue-500 font-semibold text-2xl">Welcome, what would you like to use Emailpref for?</h1>
              <h2 class="text-gray-400">We’ll ask you some questions to help us set you up!</h2>
            </div>

            <div class="flex space-x-5">
              <div
                v-for="(type, index) in typeList"
                :key="type.id"
                :class="[
                  type.active === true ? 'shadow-xl border-ep-blue-400 bg-gray-50' : 'hover:shadow-xl hover:border-ep-blue-400 ',
                  'flex flex-grow flex-col border-2 rounded-xl items-center p-5 cursor-pointer justify-center items-center',
                ]"
                @click="selectType(type, index, type.nextStep)"
              >
                <p class="font-semibold text-lg text-center">{{ type.name }}</p>
                <p class="text-gray-500 text-center">{{ type.description }}</p>
              </div>
            </div>
            <div class="flex w-full justify-center">
              <MainButton v-if="selectedType" @click="continueStep(surveyStepBuffer)">Continue</MainButton>
              <MainButton v-else :style="'grayed'">Continue</MainButton>
            </div>
          </div>
        </div>

        <div v-if="surveyStep === 'integrations-step'" class="flex flex-col h-full w-full items-center justify-center px-10">
          <div>
            <div class="flex flex-col items-center">
              <h1 class="text-ep-blue-500 font-semibold text-2xl">What tools do you use?</h1>
              <h2 class="text-gray-400">Here's a small list of some of the tools we work with and some of the most used ones in the industry.</h2>
            </div>

            <div class="flex flex-col space-y-5 w-full items-center justify-left">
              <div class="flex mt-10 flex-wrap items-center w-[75%]">
                <div
                  v-for="(integration, index) in integrationsList"
                  :key="integration.name"
                  :class="[
                    integration.active === true
                      ? 'w-fit rounded-lg bg-gray-50 mt-3 mr-5 px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-lg border-2 border-ep-blue-400 hover:bg-gray-50 cursor-pointer'
                      : 'w-fit rounded-lg bg-white mt-3 mr-5 px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm border-2 border-gray-200 hover:bg-gray-100 cursor-pointer',
                  ]"
                  @click="selectIntegration(integration.name, index)"
                >
                  {{ integration.name }}
                </div>
              </div>
              <div class="flex w-[75%]">
                <MainInput v-if="otherInputBox" v-model="extraIntegration" :name="'Other:'" :type="'text'" class="w-[50%]" />
              </div>
            </div>

            <div class="flex mt-10 w-full justify-center space-x-2">
              <MainButton :style="'secondary'" @click="backBtn('start')">Back</MainButton>
              <MainButton v-if="selectedIntegrations.length > 0" @click="continueStepIntegration('contacts-step')">Continue</MainButton>
              <MainButton v-else :style="'grayed'">Continue</MainButton>
            </div>
          </div>
        </div>

        <div v-if="surveyStep === 'contacts-step'" class="flex flex-col h-full w-full items-center justify-center px-10">
          <div class="space-y-10">
            <div class="flex flex-col items-center text-center">
              <h1 class="text-ep-blue-500 font-semibold text-2xl">How many contacts do you manage?</h1>
              <h2 class="text-gray-400">This will help us set you on the right when using our system!</h2>
            </div>

            <div class="min-w-60 flex flex-wrap items-center">
              <div
                v-for="(contact, index) in contactAmount"
                :key="contact.name"
                :class="[
                  'rounded-md p-2.5 m-2.5 text-sm font-semibold text-gray-900 cursor-pointer',
                  contact.active
                    ? 'bg-white shadow-lg border-2 border-ep-blue-400 hover:bg-gray-50'
                    : 'bg-gray-100 shadow-sm border-2 border-gray-200 hover:bg-gray-200',
                ]"
                @click="selectContact(contact, index)"
              >
                {{ contact.name }}
              </div>
            </div>

            <div class="flex w-full justify-center space-x-2">
              <MainButton :style="'secondary'" @click="backBtn(selectedIntegrations.length > 0 ? 'integrations-step' : 'start')">Back</MainButton>
              <MainButton v-if="selectedContacts" @click="continueStep('third-step')">Continue</MainButton>
              <MainButton v-else :style="'grayed'">Continue</MainButton>
            </div>
          </div>
        </div>

        <div v-if="surveyStep === 'third-step'" class="flex flex-col h-full w-full items-center justify-center px-10">
          <div class="w-full flex flex-col items-center">
            <div class="flex flex-col items-center">
              <h1 class="text-ep-blue-500 font-semibold text-2xl">Tell us about yourself</h1>
              <h2 class="font-semibold mt-5">What is your business name?</h2>
              <h3 class="text-gray-400">This is the name that will appear on your profile</h3>
            </div>

            <div class="flex mt-5 space-x-5 flex-wrap items-center w-full md:w-[650px]">
              <MainInput
                v-model="businessName"
                class="w-full"
                :name="'Your Business Name'"
                :type="'text'"
                :placeholder="'Business Name'"
                :error="inputError"
                :success="inputSuccess"
              ></MainInput>
            </div>

            <div class="flex mt-10 w-full justify-center space-x-3">
              <MainButton :style="'secondary'" @click="backBtn('contacts-step')">Back</MainButton>
              <MainButton v-if="inputSuccess" @click="continueStep('fourth-step')">Continue</MainButton>
              <MainButton v-else :style="'grayed'">Continue</MainButton>
            </div>
          </div>
        </div>

        <div v-if="surveyStep === 'fourth-step'" class="flex flex-col h-full w-full items-center justify-center px-10">
          <div class="w-full flex flex-col items-center">
            <div class="flex flex-col items-center">
              <h1 class="text-ep-blue-500 font-semibold text-2xl">Tell us about yourself</h1>
              <h2 class="font-semibold mt-5">What is your business name?</h2>
              <h3 class="text-gray-400">This is the name that will appear on your profile.</h3>
            </div>

            <div class="flex mt-5 space-x-5 flex-wrap items-center w-full md:w-[650px]">
              <MainInput
                class="w-full"
                :name="'Your Business Name'"
                :type="'text'"
                :placeholder="'Business Name'"
                disabled
                :style="'disabled'"
                :value="businessName"
              />
            </div>
          </div>

          <div class="w-full flex flex-col items-center mt-10">
            <div class="flex flex-col items-center">
              <h2 class="font-semibold">Now choose a domain!</h2>
              <h3 class="text-gray-400">This will be the default URL for your profile, preference pages and products.</h3>
            </div>

            <div class="flex mt-5 space-x-5 flex-wrap items-center w-full md:w-[650px]">
              <MainInput
                v-model="domainName"
                class="w-full"
                :name="'Your Domain Name'"
                :type="'domain'"
                :placeholder="'yourdomain.emailpref.com'"
                :error="inputError"
                :success="inputSuccess"
              />
            </div>
            <div v-if="domainName" class="text-sm text-gray-500 w-full md:w-[650px] flex">
              <Info v-if="inputError" :label="'Valid examples'" :description="validPathExamples" :position="'right'" />
            </div>

            <div class="flex mt-10 w-full justify-center space-x-3">
              <MainButton :style="'secondary'" @click="backBtn('third-step')">Back</MainButton>
              <MainButton v-if="inputSuccess" @click="finishSurvey('fifth-step')">Continue</MainButton>
              <MainButton v-else :style="'grayed'">Continue</MainButton>
            </div>
          </div>
        </div>

        <div v-if="surveyStep === 'fifth-step'" class="flex flex-col h-full w-full items-center justify-center px-10">
          <div>
            <div class="flex flex-col items-center">
              <MainLoader class="w-screen max-w-md flex-auto rounded-3xl bg-white text-sm" />
              <h1 class="text-ep-blue-500 font-semibold text-2xl">Sit tight while we set up your account!</h1>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <MainLoader v-else class="flex h-screen items-center" />
</template>

<style scoped></style>
